import { useEffect, useState } from "react"
import { createPortal } from "react-dom"

import { ResizeSeparator } from "@coval/ui"

/** Percentage points an arrow key or a step control moves the divider. */
const STEP_PCT = 2

/**
 * The split's width in px, kept current as it reflows, or 0 until the gutter
 * is placed and laid out.
 */
function useSplitWidth(gutter: HTMLDivElement | null) {
  const [width, setWidth] = useState(0)

  useEffect(() => {
    const split = gutter?.parentElement
    if (!split) {
      return
    }
    setWidth(split.getBoundingClientRect().width)
    if (typeof ResizeObserver === "undefined") {
      return
    }
    const observer = new ResizeObserver(() => {
      setWidth(split.getBoundingClientRect().width)
    })
    observer.observe(split)
    return () => observer.disconnect()
  }, [gutter])

  return width
}

/**
 * The focusable separator inside a react-split gutter. react-split owns the
 * drag, which reaches it as the press bubbles up to the gutter; the separator
 * adds the arrow keys and the tap-to-open step controls, and writes the new
 * split through `onSizesChange`, which react-split picks up from `sizes`.
 *
 * Sizes are percentages of the split's width, first pane first. Both panes
 * keep `minSizePx`, so the first pane's bounds follow the split's width.
 */
export function SplitGutterSeparator({
  gutter,
  label,
  sizes,
  defaultSizes,
  minSizePx,
  onSizesChange,
}: {
  /** The element react-split made through `useSplitGutter`. */
  gutter: HTMLDivElement | null
  /** Name of the first pane, which the separator resizes. */
  label: string
  sizes: number[]
  defaultSizes: number[]
  /** react-split's `minSize`, which holds for both panes. */
  minSizePx: number
  onSizesChange: (sizes: number[]) => void
}) {
  const splitWidth = useSplitWidth(gutter)

  if (!gutter) {
    return null
  }

  // Without a measured width (before layout) only the 0–100 bounds hold.
  const minPct =
    splitWidth > 0 ? Math.min(50, (minSizePx / splitWidth) * 100) : 0
  const maxPct = 100 - minPct

  const setFirstPaneSize = (next: number) => {
    const clamped = Math.min(maxPct, Math.max(minPct, next))
    onSizesChange([clamped, 100 - clamped])
  }

  return createPortal(
    <ResizeSeparator
      label={label}
      paneSide="before"
      value={sizes[0] ?? defaultSizes[0]}
      onValueChange={setFirstPaneSize}
      step={STEP_PCT}
      min={minPct}
      max={maxPct}
      defaultValue={defaultSizes[0]}
      formatValue={(value) => `${Math.round(value)}%`}
      // Spans the gutter's grab strip and sits above it, so a press lands on
      // the separator and bubbles on to react-split's own drag.
      className="absolute inset-y-0 -inset-x-1.5 z-[11] cursor-col-resize"
    />,
    gutter,
  )
}
